import React, { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { FileBarChart, RefreshCw, CheckCircle2, XCircle, TrendingUp, Coins } from "lucide-react";

const fmt = (n) => (Number(n) || 0).toLocaleString("ar-EG", { maximumFractionDigits: 2 });
const CAT_LABEL = (c) => c || "غير مصنّف";

export default function FinancialStatements() {
  const [tab, setTab] = useState("bs");
  const [bs, setBs] = useState(null);
  const [cf, setCf] = useState(null);
  const [is, setIs] = useState(null);
  const [fx, setFx] = useState(null);
  const [fxBook, setFxBook] = useState({});
  const [asOf, setAsOf] = useState(new Date().toISOString().slice(0, 10));
  const [from, setFrom] = useState(new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  const loadBS = async () => {
    try { const { data } = await api.get("/reports/balance-sheet", { params: { as_of: asOf } }); setBs(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadCF = async () => {
    try { const { data } = await api.get("/reports/cashflow", { params: { from_date: from, to_date: to } }); setCf(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadIS = async () => {
    try { const { data } = await api.get("/reports/pnl", { params: { from_date: from, to_date: to } }); setIs(data); }
    catch (e) { toast.error(formatErr(e)); }
  };
  const loadFX = async () => {
    try {
      const { data } = await api.get("/reports/fx-revaluation");
      setFx(data);
      const book = {}; (data.items || []).forEach((it, i) => { book[i] = it.rate; });
      setFxBook(book);
    } catch (e) { toast.error(formatErr(e)); }
  };
  const postFx = async () => {
    const book_rates = {};
    (fx?.items || []).forEach((it, i) => {
      const v = fxBook[i];
      if (v !== "" && v != null && Number(v) !== it.rate) book_rates[it.currency] = Number(v);
    });
    if (Object.keys(book_rates).length === 0) { toast.error("أدخل «سعر الدفتر» مختلفاً عن سعر اليوم لصنف واحد على الأقل"); return; }
    try {
      const { data } = await api.post("/reports/fx-revaluation/post", { book_rates });
      if (data.ok) { toast.success(`تم ترحيل قيد الفروقات (${data.kind}: ${Math.abs(data.total_gain_loss).toLocaleString("ar-EG")})`); loadFX(); }
      else toast.error(data.message || "لا يوجد فرق لترحيله");
    } catch (e) { toast.error(formatErr(e)); }
  };
  useEffect(() => { loadBS(); loadCF(); loadIS(); loadFX(); }, []); // eslint-disable-line

  const TabBtn = ({ v, children, testid }) => (
    <button onClick={() => setTab(v)} className={`px-4 py-1.5 rounded-md text-sm font-bold transition-colors ${tab === v ? "bg-primary text-primary-foreground" : "hover:bg-background/60"}`} data-testid={testid}>{children}</button>
  );

  return (
    <div className="space-y-4" data-testid="fin-statements-page">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-md bg-primary grid place-items-center text-primary-foreground"><FileBarChart className="w-5 h-5" /></div>
        <div className="flex-1">
          <h1 className="text-2xl md:text-3xl font-heading font-bold">القوائم المالية</h1>
          <p className="text-sm text-muted-foreground">الميزانية العمومية · قائمة الدخل · التدفقات النقدية · فروقات الصرف</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-1 p-1 bg-muted rounded-lg w-fit">
        <TabBtn v="bs" testid="tab-bs">الميزانية العمومية</TabBtn>
        <TabBtn v="is" testid="tab-is">قائمة الدخل</TabBtn>
        <TabBtn v="cf" testid="tab-cf">التدفقات النقدية</TabBtn>
        <TabBtn v="fx" testid="tab-fx">فروقات الصرف</TabBtn>
      </div>

      {tab === "bs" && (
        <div className="space-y-3">
          <div className="flex items-end gap-2">
            <div><Label>حتى تاريخ</Label><Input type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} data-testid="bs-date" /></div>
            <Button onClick={loadBS} variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" /> تحديث</Button>
            {bs && (
              <div className={`text-xs px-3 py-1.5 rounded font-bold flex items-center gap-1 ${bs.balanced ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>
                {bs.balanced ? <><CheckCircle2 className="w-3 h-3" /> ميزانية متوازنة</> : <><XCircle className="w-3 h-3" /> غير متوازنة</>}
              </div>
            )}
          </div>
          {bs && (
            <div className="grid md:grid-cols-2 gap-3">
              <Card className="p-4">
                <div className="font-heading font-bold text-lg mb-2 text-emerald-800">الأصول</div>
                {bs.assets.map(a => <div key={a.code} className="flex justify-between text-sm py-1 border-b border-border/50"><span>{a.code} — {a.name}</span><span className="font-mono">{fmt(a.balance)}</span></div>)}
                <div className="flex justify-between font-bold pt-2 mt-2 border-t-2 border-emerald-500"><span>إجمالي الأصول</span><span className="font-mono text-emerald-700">{fmt(bs.total_assets)}</span></div>
              </Card>
              <Card className="p-4">
                <div className="font-heading font-bold text-lg mb-2 text-primary">الخصوم وحقوق الملكية</div>
                <div className="text-xs text-muted-foreground mt-1">الخصوم</div>
                {bs.liabilities.map(a => <div key={a.code} className="flex justify-between text-sm py-1 border-b border-border/50"><span>{a.code} — {a.name}</span><span className="font-mono">{fmt(a.balance)}</span></div>)}
                <div className="flex justify-between font-bold text-primary py-1"><span>إجمالي الخصوم</span><span className="font-mono">{fmt(bs.total_liabilities)}</span></div>
                <div className="text-xs text-muted-foreground mt-3">حقوق الملكية</div>
                {bs.equity.map(a => <div key={a.code} className="flex justify-between text-sm py-1 border-b border-border/50"><span>{a.code} — {a.name}</span><span className="font-mono">{fmt(a.balance)}</span></div>)}
                <div className="flex justify-between font-bold text-primary py-1"><span>إجمالي حقوق الملكية</span><span className="font-mono">{fmt(bs.total_equity)}</span></div>
                <div className="flex justify-between font-bold pt-2 mt-2 border-t-2 border-primary"><span>الإجمالي</span><span className="font-mono">{fmt(bs.total_liabilities + bs.total_equity)}</span></div>
              </Card>
            </div>
          )}
        </div>
      )}

      {tab === "is" && (
        <div className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label>من</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="is-from" /></div>
            <div><Label>إلى</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="is-to" /></div>
            <Button onClick={loadIS} variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" /> تحديث</Button>
          </div>
          {is && (
            <Card className="p-5 max-w-2xl" data-testid="income-statement-card">
              <div className="flex items-center gap-2 mb-3 text-primary font-heading font-bold text-lg"><TrendingUp className="w-5 h-5" /> قائمة الدخل</div>
              <div className="flex justify-between text-sm py-1.5 border-b border-border/50"><span>الإيرادات (المبيعات)</span><span className="font-mono text-emerald-700">{fmt(is.revenue)}</span></div>
              <div className="flex justify-between text-sm py-1.5 border-b border-border/50"><span>تكلفة البضاعة المباعة</span><span className="font-mono text-red-700">({fmt(is.cogs)})</span></div>
              <div className="flex justify-between font-bold py-2 border-b-2 border-primary/40"><span>مجمل الربح</span><span className={`font-mono ${is.gross_profit >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(is.gross_profit)}</span></div>
              <div className="text-xs text-muted-foreground mt-3 mb-1">المصروفات التشغيلية</div>
              {Object.keys(is.expenses || {}).length === 0 && <div className="text-xs text-muted-foreground py-1">لا توجد مصروفات مسجلة</div>}
              {Object.entries(is.expenses || {}).map(([cat, amt]) => (
                <div key={cat} className="flex justify-between text-sm py-1 border-b border-border/50"><span>{CAT_LABEL(cat)}</span><span className="font-mono text-red-700">({fmt(amt)})</span></div>
              ))}
              <div className="flex justify-between text-sm py-1.5 font-semibold"><span>إجمالي المصروفات</span><span className="font-mono text-red-700">({fmt(is.total_expenses)})</span></div>
              <div className={`flex justify-between items-center font-heading font-bold text-lg py-3 mt-2 px-3 rounded-md ${is.net_profit >= 0 ? "bg-emerald-50 text-emerald-800" : "bg-red-50 text-red-800"}`} data-testid="net-profit-row">
                <span>{is.net_profit >= 0 ? "صافي الربح" : "صافي الخسارة"}</span>
                <span className="font-mono">{fmt(is.net_profit)}</span>
              </div>
            </Card>
          )}
        </div>
      )}

      {tab === "cf" && (
        <div className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <div><Label>من</Label><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="cf-from" /></div>
            <div><Label>إلى</Label><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="cf-to" /></div>
            <Button onClick={loadCF} variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" /> تحديث</Button>
          </div>
          {cf && (
            <div className="space-y-3">
              <Card className="p-4">
                <div className="font-heading font-bold text-emerald-800 mb-2">الأنشطة التشغيلية</div>
                <div className="flex justify-between text-sm py-1"><span>مقبوضات المبيعات النقدية</span><span className="font-mono text-emerald-700">+{fmt(cf.operating.sales_cash)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>مرتجعات مشتريات مستلمة</span><span className="font-mono text-emerald-700">+{fmt(cf.operating.purchase_returns_received)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>سندات قبض</span><span className="font-mono text-emerald-700">+{fmt(cf.operating.vouchers_received)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>مدفوعات المشتريات النقدية</span><span className="font-mono text-red-700">-{fmt(cf.operating.purchases_cash)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>مرتجعات مبيعات مدفوعة</span><span className="font-mono text-red-700">-{fmt(cf.operating.sales_returns_paid)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>سندات صرف</span><span className="font-mono text-red-700">-{fmt(cf.operating.vouchers_paid)}</span></div>
                <div className="flex justify-between text-sm py-1"><span>المصروفات</span><span className="font-mono text-red-700">-{fmt(cf.operating.expenses)}</span></div>
                <div className="flex justify-between font-bold pt-2 mt-2 border-t border-emerald-200"><span>صافي التشغيلي</span><span className={`font-mono ${cf.operating.total >= 0 ? "text-emerald-700" : "text-red-700"}`}>{cf.operating.total >= 0 ? "+" : ""}{fmt(cf.operating.total)}</span></div>
              </Card>
              <Card className="p-4">
                <div className="font-heading font-bold text-primary mb-2">الأنشطة الاستثمارية</div>
                <div className="flex justify-between text-sm py-1"><span>شراء أصول ثابتة</span><span className="font-mono text-red-700">-{fmt(cf.investing.fixed_assets_purchased)}</span></div>
                <div className="flex justify-between font-bold pt-2 mt-2 border-t border-primary/20"><span>صافي الاستثماري</span><span className={`font-mono ${cf.investing.total >= 0 ? "text-emerald-700" : "text-red-700"}`}>{fmt(cf.investing.total)}</span></div>
              </Card>
              <Card className="p-6 bg-primary/5 border-primary/30">
                <div className="flex justify-between font-heading font-bold text-lg"><span>صافي التغير في النقدية</span><span className={`font-mono ${cf.net_cash >= 0 ? "text-emerald-700" : "text-red-700"}`}>{cf.net_cash >= 0 ? "+" : ""}{fmt(cf.net_cash)}</span></div>
              </Card>
            </div>
          )}
        </div>
      )}

      {tab === "fx" && (
        <div className="space-y-3">
          <div className="flex items-end gap-2 flex-wrap">
            <Button onClick={loadFX} variant="outline" className="gap-1"><RefreshCw className="w-3 h-3" /> تحديث</Button>
            {fx && <span className="text-xs text-muted-foreground self-center">العملة الأساسية: <b>{fx.base}</b> · أسعار اليوم اليدوية</span>}
          </div>
          <Card className="p-4" data-testid="fx-reval-card">
            <div className="flex items-center gap-2 mb-1 text-primary font-heading font-bold text-lg"><Coins className="w-5 h-5" /> إعادة تقييم الأرصدة بالعملات الأجنبية</div>
            <p className="text-xs text-muted-foreground mb-3">يعرض أرصدة الصناديق والبنوك بالعملات الأجنبية مقوّمة بالعملة الأساسية بسعر اليوم. أدخل «سعر الدفتر» (السعر عند آخر تقييم) لمعرفة الربح/الخسارة غير المحقق من فروقات الصرف.</p>
            {fx && fx.items.length === 0 && <div className="text-sm text-muted-foreground py-6 text-center">لا توجد أرصدة بعملات أجنبية (كل الأرصدة بالعملة الأساسية {fx.base}).</div>}
            {fx && fx.items.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-xs border-b border-border">
                      <th className="text-start py-2">الحساب</th>
                      <th className="text-start py-2">العملة</th>
                      <th className="text-end py-2">الرصيد</th>
                      <th className="text-end py-2">سعر اليوم</th>
                      <th className="text-end py-2">القيمة بـ{fx.base}</th>
                      <th className="text-end py-2 w-28">سعر الدفتر</th>
                      <th className="text-end py-2">فرق (غير محقق)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fx.items.map((it, i) => {
                      const book = fxBook[i] === "" || fxBook[i] === undefined ? it.rate : Number(fxBook[i]);
                      const diff = it.balance * (it.rate - book);
                      return (
                        <tr key={i} className="border-b border-border/50" data-testid={`fx-row-${i}`}>
                          <td className="py-2 font-semibold">{it.name} <span className="text-xs text-muted-foreground">({it.kind === "bank" ? "بنك" : "صندوق"})</span></td>
                          <td className="py-2">{it.currency}</td>
                          <td className="py-2 text-end font-mono">{fmt(it.balance)}</td>
                          <td className="py-2 text-end font-mono">{fmt(it.rate)}</td>
                          <td className="py-2 text-end font-mono text-primary">{fmt(it.base_value)}</td>
                          <td className="py-2 text-end">
                            <Input type="number" step="any" value={fxBook[i] ?? ""} onChange={(e) => setFxBook({ ...fxBook, [i]: e.target.value })} className="h-8 text-end tabular-nums" data-testid={`fx-book-${i}`} />
                          </td>
                          <td className={`py-2 text-end font-mono font-bold ${diff > 0 ? "text-emerald-700" : diff < 0 ? "text-red-700" : ""}`}>{diff >= 0 ? "+" : ""}{fmt(diff)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="font-bold">
                      <td className="py-2" colSpan={4}>الإجمالي بالعملة الأساسية</td>
                      <td className="py-2 text-end font-mono text-primary">{fmt(fx.total_base_value)}</td>
                      <td></td>
                      <td className="py-2 text-end font-mono">{(() => {
                        const t = fx.items.reduce((s, it, i) => { const book = fxBook[i] === "" || fxBook[i] === undefined ? it.rate : Number(fxBook[i]); return s + it.balance * (it.rate - book); }, 0);
                        return (t >= 0 ? "+" : "") + fmt(t);
                      })()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
            {fx && fx.items.length > 0 && (
              <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
                <p className="text-[11px] text-muted-foreground">القيد يُرحَّل إلى حساب «أرباح/خسائر فروق أسعار الصرف» مقابل الصندوق/البنك.</p>
                <Button onClick={postFx} className="gap-1 bg-primary" data-testid="fx-post-button">
                  <FileBarChart className="w-4 h-4" /> ترحيل قيد الفروقات
                </Button>
              </div>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
